import InnerJoinIcon from "./icons/InnerJoinIcon";
import LeftJoinIcon from "./icons/LeftJoinIcon";
import OuterJoinIcon from "./icons/OuterJoinIcon";
import { PgTypeIcon } from "./icons/PgTypeIcon";
import RightJoinIcon from "./icons/RightJoinIcon";
import { Column, JoinOnOperator, JoinType, QueryJoin, QueryJoinOnClause, QueryTable } from "@avenueops/visual-query-editor";
import {
  Button,
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
  OptionGroup,
  OptionGrouping,
  SmartForm,
  SteppedDropdown,
} from "@avenueops/web-components";
import { TrashIcon } from "@heroicons/react/24/outline";
import { TableIcon } from "@radix-ui/react-icons";
import { Controller, FieldErrors, useFieldArray, useFormContext, useWatch } from "react-hook-form";

function getJoinSvg(type: JoinType) {
  switch (type) {
    case JoinType.INNER:
      return <InnerJoinIcon className="w-6 h-6" />;
    case JoinType.LEFT:
      return <LeftJoinIcon className="w-6 h-6" />;
    case JoinType.RIGHT:
      return <RightJoinIcon className="w-6 h-6" />;
    case JoinType.FULL:
      return <OuterJoinIcon className="w-6 h-6" />;
    default:
      return null;
  }
}

function getJoinOperatorLabel(operator: JoinOnOperator) {
  switch (operator) {
    case JoinOnOperator.EQUALS:
      return "Equals";
    case JoinOnOperator.GREATER_THAN:
      return "Greater Than";
    case JoinOnOperator.GREATER_THAN_OR_EQUAL:
      return "Greater Than or Equal To";
    case JoinOnOperator.LESS_THAN:
      return "Less Than";
    case JoinOnOperator.LESS_THAN_OR_EQUAL:
      return "Less Than or Equal To";
    case JoinOnOperator.NOT_EQUALS:
      return "Not Equal To";
    default:
      return null;
  }
}

interface Props {
  index: number;
  prefix: string;
  tableOptions: OptionGrouping<{
    name: string;
    columns: string[];
  }>;
  columnOptions?: OptionGroup<string>[];
  removeSelf: () => void;
}

export default function JoinQueryCard({ prefix: basePrefix, index, tableOptions, columnOptions = [], removeSelf }: Props) {
  const { watch, control, setValue, register } = useFormContext();
  const field = watch(`${basePrefix}.joins.${index}`) as QueryJoin;
  register(`${basePrefix}.joins.${index}`, { shouldUnregister: true });
  const onClauseFieldArray = useFieldArray({
    control,
    name: `${basePrefix}.joins.${index}.on`,
  });

  const primaryTable = useWatch({ name: `${basePrefix}.primaryTable` }) as QueryTable;
  // The id field is added by the useFieldArray hook and used for the "key" prop
  const onClauses = useWatch({ name: `${basePrefix}.joins.${index}.on` }) as (QueryJoinOnClause & { id: string })[];

  const prefix = `${basePrefix}.joins.${index}`;
  const { table, type, on } = field;
  return (
    <div className="flex gap-2">
      <Collapsible className="bg-white rounded drop-shadow-sm px-4 flex-1 overflow-hidden" defaultOpen={true}>
        <CollapsibleTrigger>
          <p className="text-sm flex items-center text-gray-500">
            <TableIcon className="w-4 h-4 mr-2" />
            {table.name ? table.name : "Select data"}
          </p>
        </CollapsibleTrigger>
        <CollapsibleContent className="flex flex-col gap-2">
          <div className="flex items-center gap-2">
            <div className="flex-1 h-[40px] bg-gray-100 rounded flex flex-row gap-2 items-center px-2 border text-sm font-normal border-cool-gray-300 overflow-hidden">
              {index === 0 ? primaryTable.name : "Previous Results"}
            </div>
            <div>
              <SmartForm.DropdownSelect
                name={`${prefix}.type`}
                type="INLINE"
                usePortal
                defaultValue={JoinType.INNER}
                hideSelectedLabel
                options={Object.values(JoinType).map((joinType) => ({
                  label: joinType,
                  value: joinType,
                  icon: getJoinSvg(joinType),
                }))}
              />
            </div>
            <div className="flex-1 relative overflow-hidden">
              <Controller
                name={`${prefix}.table`}
                control={control}
                render={({ field: tableField, fieldState }) => {
                  return (
                    <SteppedDropdown<{
                      name: string;
                      columns: string[];
                    }>
                      onBlur={tableField.onBlur}
                      error={fieldState.error as unknown as FieldErrors}
                      name={`${prefix}.table`}
                      options={tableOptions}
                      defaultValue={{
                        name: tableField.value.name,
                        columns: tableField.value?.columns?.map((column: Column) => column.name),
                      }}
                      onChange={(val, options) => {
                        const tableOption = options?.name as unknown as { value: string; metaData: { schemaName: string } };
                        const columnOptions = options?.columns as unknown as
                          | { value: string; metaData: { data_type: string; enum_name?: string } }[]
                          | undefined;
                        tableField.onChange({
                          name: val.name,
                          foo: "bar",
                          schemaName: tableOption?.metaData?.schemaName,
                          columns:
                            columnOptions?.map((column) => ({
                              name: column.value,
                              schemaName: tableOption?.metaData?.schemaName,
                              tableName: val.name,
                              enumName: column.metaData?.enum_name,
                              type: column.metaData?.data_type ?? "unknown",
                            })) ?? [],
                        });
                      }}
                    />
                  );
                }}
              />
            </div>
          </div>
          <p className="text-gray-800">ON</p>
          {onClauses && onClauses.length > 0
            ? onClauses.map((onClause, clauseIndex) => (
                <div key={onClause.id} className="flex flex-col">
                  {clauseIndex > 0 ? (
                    <div className="flex justify-end">
                      <div className="flex-1 flex items-center justify-center w-8 h-8">
                        <p className="text-gray-500">AND</p>
                      </div>
                      <TrashIcon className="w-5 h-5 text-gray-500 hover:text-red-600 cursor-pointer" onClick={() => onClauseFieldArray.remove(clauseIndex)} />
                    </div>
                  ) : null}
                  <div className="flex items-center">
                    <div className="flex-1 overflow-hidden">
                      <SmartForm.DropdownSelect
                        name={`${prefix}.on.${clauseIndex}.left.name`}
                        usePortal
                        options={columnOptions?.filter((optionGroup) => optionGroup.label !== table.name) ?? []}
                        disabled={!primaryTable || !primaryTable.name || !primaryTable.columns}
                        onChangeCallback={(val, selectedOption) => {
                          if (selectedOption && selectedOption[0] && selectedOption[0].meta?.data_type) {
                            setValue(`${prefix}.on.${clauseIndex}.left.type`, selectedOption[0].meta.data_type as string);
                            setValue(`${prefix}.on.${clauseIndex}.left.tableName`, selectedOption[0].meta.tableName as string);
                            setValue(`${prefix}.on.${clauseIndex}.left.schemaName`, selectedOption[0].meta.schemaName as string);
                            setValue(`${prefix}.on.${clauseIndex}.left.enumName`, selectedOption[0].meta.enumName as string | undefined);
                          }
                        }}
                      />
                    </div>
                    <span>
                      <SmartForm.DropdownSelect
                        name={`${prefix}.on.${clauseIndex}.operator`}
                        type="INLINE"
                        usePortal
                        defaultValue={JoinOnOperator.EQUALS}
                        hideSelectedLabel
                        options={Object.values(JoinOnOperator).map((operator) => ({
                          label: getJoinOperatorLabel(operator) ?? "",
                          value: operator,
                          icon: <span className="text-lg px-2">{operator}</span>,
                        }))}
                      />
                    </span>
                    <div className="flex-1 overflow-hidden">
                      <SmartForm.DropdownSelect
                        name={`${prefix}.on.${clauseIndex}.right.name`}
                        usePortal
                        options={
                          table && table.columns
                            ? table.columns.map((column) => ({
                                label: column.name,
                                value: column.name,
                                icon: <PgTypeIcon pgType={column.type} className="w-4 h-4" />,
                                meta: {
                                  data_type: column.type,
                                  tableName: table.name,
                                  schemaName: table.schemaName,
                                  enumName: column.enumName,
                                },
                              }))
                            : []
                        }
                        onChangeCallback={(val, selectedOption) => {
                          if (selectedOption && selectedOption[0] && selectedOption[0].meta?.data_type) {
                            setValue(`${prefix}.on.${clauseIndex}.right.type`, selectedOption[0].meta.data_type as string);
                            setValue(`${prefix}.on.${clauseIndex}.right.tableName`, selectedOption[0].meta.tableName as string);
                            setValue(`${prefix}.on.${clauseIndex}.right.schemaName`, selectedOption[0].meta.schemaName as string);
                            setValue(`${prefix}.on.${clauseIndex}.right.enumName`, selectedOption[0].meta.enumName as string | undefined);
                          }
                        }}
                        disabled={!table || !table.name || !table.columns}
                      />
                    </div>
                  </div>
                </div>
              ))
            : null}
          <div className="flex justify-end">
            <Button type="TEXT" onClick={() => onClauseFieldArray.append({})}>
              + Add
            </Button>
          </div>
        </CollapsibleContent>
      </Collapsible>
      <TrashIcon className="mt-2 w-5 h-5 text-gray-500 hover:text-red-600 cursor-pointer" onClick={() => removeSelf()} />
    </div>
  );
}
